import React, { useState, useEffect, useRef } from 'react';
import styles from '../../../Dashboard/index.module.less';
import {
  Button,
  Col,
  Row,
  Typography,
  DatePicker,
  Card,
  Space,
  Tag,
  Input,
  Badge,
  Form,
  Radio,
  Empty
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  StarFilled,
  CaretRightOutlined,
  WarningOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DownOutlined,
  UpOutlined,
  CheckCircleFilled,
  SmileOutlined,
  // FrownOutlined,
  LikeOutlined,
  UnorderedListOutlined
  //   CaretDownOutlined,
  //   CaretUpOutlined
} from '@ant-design/icons';
import CustomButton from '@/common/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMyTasks,
  selectPlansSlice,
  updateTaskStatus
} from '../../redux/slice';
import CustomModal from '@/common/CustomModal';
import FormItem from '@/common/FormItem';
import CountCard from 'pages/AllEmployeeTasks/components/CountCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const { Text, Title } = Typography;
const { TextArea } = Input;

// Returns today, or the previous Friday if today is Saturday or Sunday
const getEffectiveToday = () => {
  const d = new Date();
  const day = d.getDay();
  if (day === 0) d.setDate(d.getDate() - 2);
  else if (day === 6) d.setDate(d.getDate() - 1);
  return d;
};

const TaskTracker = () => {
  const [currentDate, setCurrentDate] = useState(getEffectiveToday);
  const currentDateRef = useRef(currentDate);
  currentDateRef.current = currentDate;
  const [open, setOpen] = useState(false);
  const [pendingTasksVisible, setPendingTasksVisible] = useState(true);
  const [escalatedTasksVisible, setEscalatedTasksVisible] = useState(true);
  const [priorityTasks, setPriorityTasks] = useState([]);
  const [planId, setPlanId] = useState('');
  const [planStatus, setPlanStatus] = useState('');
  const [showModel, setShowModel] = useState(false);
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [pendingTasks, setPendingTasks] = useState([]);
  const [escalatedTasks, setEscalatedTasks] = useState([]);
  const [completedTasksVisible, setCompletedTasksVisible] = useState(true);
  const [completeTasks, setCompleteTasks] = useState([]);
  const [statusTitle, setStatusTitle] = useState('');
  const [aIuse, setAIuse] = useState('');
  const [taskStats, setTaskStats] = useState({});
  const { loading } = useSelector(selectPlansSlice);

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const stuck_reasons = [
    { value: 'specifications', label: 'Missing or unclear specifications' },
    { value: 'dependency', label: 'Dependency on another team member' },
    { value: 'technical', label: 'Facing a technical challenge' },
    { value: 'access', label: 'Lacking necessary access' },
    { value: 'thirdParty', label: 'Problems with third-party services' },
    { value: 'other', label: 'Other' }
  ];
  useEffect(() => {
    handleGetMyTasks();
  }, [currentDate]);

  const handleGetMyTasks = () => {
    const planDate = moment(currentDate).format('YYYY-MM-DD');
    const payload = { plan_date: planDate };

    setPriorityTasks([]);
    setPendingTasks([]);
    setEscalatedTasks([]);
    setCompleteTasks([]);
    setTaskStats({});

    dispatch(getMyTasks(payload)).then((res) => {
      const selectedDate = moment(currentDateRef.current).format('YYYY-MM-DD');
      if (selectedDate !== planDate) return;

      if (res?.payload?.success) {
        const data = res?.payload?.data ?? [];
        let priority_tasks = data.filter(
          (item) => item?.task_status === 'assigned' && item?.is_highlight
        );
        let pending_tasks = data.filter(
          (item) => item?.task_status === 'assigned' && !item?.is_highlight
        );
        let escalted_tasks = data.filter(
          (item) => item?.task_status === 'escalated'
        );
        let completed_tasks = data.filter(
          (item) => item?.task_status === 'completed'
        );
        setPriorityTasks(priority_tasks);
        setPendingTasks(pending_tasks);
        setEscalatedTasks(escalted_tasks);
        setCompleteTasks(completed_tasks);
        setTaskStats(res?.payload?.task_statistics ?? {});
      }
    });
  };

  const handleReasonChange = (e) => {
    setReason(e);
  };

  const handleOtherReasonChange = (e) => {
    setOtherReason(e.target.value);
  };

  const handleReason = (e) => {
    setReason(e.target.value);
  };

  const handleAIConfirm = (e) => {
    setAIuse(e.target.value);
  };

  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const handleDateChange = (date) => {
    if (date) setCurrentDate(date.toDate());
  };

  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 1);
    while (newDate.getDay() === 0 || newDate.getDay() === 6) {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNextDay = () => {
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 1);
    while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    const effectiveToday = getEffectiveToday();
    effectiveToday.setHours(0, 0, 0, 0);
    nextDate.setHours(0, 0, 0, 0);
    if (nextDate <= effectiveToday) setCurrentDate(nextDate);
  };

  const effectiveToday = getEffectiveToday();
  const isCurrentDateToday = moment(currentDate).isSame(
    moment(effectiveToday),
    'day'
  );

  const disabledDate = (current) => {
    if (!current) return false;
    const day = moment(current).day();
    if (day === 0 || day === 6) return true;
    return moment(current).isAfter(moment(effectiveToday), 'day');
  };

  // Toggle pending tasks visibility
  const togglePendingTasks = () => {
    setPendingTasksVisible(!pendingTasksVisible);
  };

  const toggleescalatedTasks = () => {
    setEscalatedTasksVisible(!escalatedTasksVisible);
  };

  const toggleCompletedTasks = () => {
    setCompletedTasksVisible(!completedTasksVisible);
  };

  const StartTask = (id, status) => {
    const payload = { plan_id: id, status: status };
    dispatch(updateTaskStatus(payload)).then((res) => {
      if (res?.payload?.success) {
        handleGetMyTasks();
      }
    });
  };

  const handleStuck = (id, status, title) => {
    setPlanId(id);
    setPlanStatus(status);
    setShowModel(true);
    setStatusTitle(title);
  };

  const handleClose = () => {
    setShowModel(false);
    form.resetFields();
    setAIuse(false);
    setReason('');
    setOtherReason('');
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(() => {
        const payload =
          planStatus === 'completed'
            ? {
                plan_id: planId,
                status: planStatus,
                close_notes: otherReason,
                ai_use: aIuse === 'yes' ? true : false,
                difficulty: reason
              }
            : {
                plan_id: planId,
                status: planStatus,
                stuck_comments: reason === 'other' ? otherReason : reason
              };
        dispatch(updateTaskStatus(payload)).then((res) => {
          if (res?.payload?.success) {
            handleGetMyTasks();
            setShowModel(false);
            form.resetFields();
            setAIuse(false);
            setReason('');
            setOtherReason('');
          }
        });
      })
      .catch((err) => {});
  };

  const Metrics = [
    {
      label: 'Total Tasks',
      count: taskStats?.total_tasks || 0,
      icon: <UnorderedListOutlined />,
      iconBg: '#e6f7ff',
      iconColor: 'rgb(76, 0, 255)',
      countColor: '#000000'
    },
    {
      label: 'Completed',
      count: taskStats?.completed_tasks || 0,
      icon: <CheckOutlined />,
      iconBg: '#d9f7be',
      iconColor: '#52c41a',
      countColor: '#52c41a'
    },
    {
      label: 'Remaining',
      count: taskStats?.remaining_tasks || 0,
      icon: <ClockCircleOutlined />,
      iconBg: '#fff7e6',
      iconColor: '#fa8c16',
      countColor: '#1890ff'
    },
    {
      label: 'Released',
      count: taskStats?.released_tasks || 0,
      icon: (
        <FontAwesomeIcon icon={faRedo} style={{ transform: 'scaleX(-1)' }} />
      ),
      iconBg: '#fef9c3',
      iconColor: '#ca8a04',
      countColor: '#ca8a04'
    }
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <div className={'flex items-center'}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            className={'!text-sm md:!text-[16px]  font-bold !m-0'}
            onClick={goToPreviousDay}
          />
          <Text className={'!text-sm md:!text-[16px]  font-bold !m-0'}>
            {formattedDate}
          </Text>
          <Button
            type="text"
            icon={<RightOutlined />}
            className={'!text-[16px]  font-bold !m-0'}
            onClick={goToNextDay}
            disabled={isCurrentDateToday}
          />
        </div>
        {/* <Col span={1} style={{ marginRight: '-12px' }}></Col> */}
        <div className="flex">
          <DatePicker
            value={moment(currentDate)}
            onChange={handleDateChange}
            placeholder="Select Date"
            disabledDate={disabledDate}
          />
        </div>
      </div>

      <div className={'mt-3 grid-cols-1 grid sm:grid-cols-4 gap-2 sm:gap-6'}>
        {Metrics.map((card) => {
          return <CountCard key={card.label} {...card} />;
        })}
      </div>
      <br />
      {priorityTasks?.length ? (
        <Row gutter={24} className={`${styles.RowStyles} !pb-8`}>
          <Col span={24}>
            <Card className={styles.taskCard} bordered={false}>
              <Row gutter={24}>
                <Col span={24}>
                  <Title
                    level={5}
                    className={styles.taskTitle}
                    style={{ margin: 0 }}
                  >
                    <Space>
                      {<StarFilled className={styles.starIconStyle} />}
                      {`Today's Highlight`}
                    </Space>
                  </Title>
                </Col>
              </Row>
              {priorityTasks?.length ? (
                <div className={'!mt-1.5 bg-[#f9fafb] !rounded-lg'}>
                  <div className="flex items-center justify-between px-3 pt-2.5">
                    <div>
                      <Title
                        level={5}
                        className={styles.taskTitle}
                        style={{ margin: 0 }}
                      >
                        <Space>{priorityTasks?.[0]?.task?.title}</Space>
                      </Title>
                    </div>
                    <div>
                      {priorityTasks?.[0]?.task?.task_type === 'ad_hoc' && (
                        <Tag color="purple" className="!m-0" bordered={false}>
                          Ad-hoc
                        </Tag>
                      )}
                      <Tag bordered={false} color="red">
                        Priority
                      </Tag>
                    </div>
                  </div>
                  <div className="px-3">
                    <Text type="secondary" className={styles.descriptionText}>
                      {priorityTasks?.[0]?.task?.description}
                    </Text>
                  </div>
                  <div className="px-3">
                    <Text type="secondary" className={styles.secondaryText}>
                      {priorityTasks?.[0]?.task?.client?.client_name} ·{' '}
                      {priorityTasks?.[0]?.task?.project?.project} ·{' '}
                      {`${priorityTasks?.[0]?.assigner?.first_name} ${priorityTasks?.[0]?.assigner?.last_name}`}{' '}
                      · {`T${priorityTasks?.[0]?.task_id}`}
                    </Text>
                  </div>
                  <div className={'mt-3 pb-6 px-3'}>
                    <div>
                      <Space>
                        {/*Commenting out because users did not find this useful*/}

                        {/* <CustomButton
                          className={styles.primaryButton}
                          size="small"
                          onClick={() =>
                            StartTask(priorityTasks?.[0]?.id, 'started')
                          }
                          disabled={priorityTasks?.[0]?.is_started}
                          // type="primary"
                          // ghost
                        >
                          <Space size={4}>
                            <CaretRightOutlined />{' '}
                            {priorityTasks?.[0]?.is_started
                              ? 'Started'
                              : 'Start'}
                          </Space>
                        </CustomButton> */}
                        <CustomButton
                          className={styles.stuckButton}
                          size="small"
                          disabled={!isCurrentDateToday}
                          onClick={() =>
                            handleStuck(priorityTasks?.[0]?.id, 'escalated')
                          }
                        >
                          <Space size={4}>
                            <WarningOutlined /> Stuck
                          </Space>
                        </CustomButton>
                        <CustomButton
                          className={styles.doneButton}
                          size="small"
                          disabled={!isCurrentDateToday}
                          onClick={() =>
                            handleStuck(
                              priorityTasks?.[0]?.id,
                              'completed',
                              priorityTasks?.[0]?.title
                            )
                          }
                        >
                          <Space size={4}>
                            <CheckOutlined /> Done
                          </Space>
                        </CustomButton>
                      </Space>
                    </div>
                  </div>
                </div>
              ) : (
                ''
              )}
            </Card>
          </Col>
        </Row>
      ) : (
        ''
      )}
      {pendingTasks?.length ? (
        <Row gutter={24} className={`${styles.RowStyles} !pb-8`}>
          <Col span={24}>
            <Card className={styles.taskCard} bordered={false}>
              <Row gutter={24}>
                <Col span={20}>
                  <Title
                    level={5}
                    className={styles.taskTitle}
                    style={{ margin: 0 }}
                  >
                    <Space>
                      {<ClockCircleOutlined className={styles.starIconStyle} />}
                      {`Pending Tasks`}
                    </Space>
                  </Title>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                  <Space align="center" size={4} className={styles.caret}>
                    <Badge
                      count={parseInt(pendingTasks?.length)}
                      className={styles.BadgeIconStyle}
                    />
                    {pendingTasksVisible ? (
                      <DownOutlined onClick={togglePendingTasks} />
                    ) : (
                      <UpOutlined onClick={togglePendingTasks} />
                    )}
                  </Space>
                </Col>
              </Row>
              {pendingTasksVisible &&
                pendingTasks?.map((item, index) => {
                  const isAdHOc = item.task.task_type === 'ad_hoc';

                  return (
                    <Card
                      className={styles.nestedCard}
                      bordered={false}
                      key={index}
                    >
                      <Row gutter={24}>
                        <Col
                          span={24}
                          className="!flex !items-center !justify-between"
                        >
                          <Title
                            level={5}
                            className={styles.taskTitle}
                            style={{ margin: 0 }}
                          >
                            <Space>{item?.task?.title}</Space>
                          </Title>
                          {isAdHOc && (
                            <Tag
                              color="purple"
                              className="!m-0"
                              bordered={false}
                            >
                              Ad-hoc
                            </Tag>
                          )}
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.descriptionText}
                          >
                            {item?.task?.description}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.secondaryText}
                          >
                            {item?.task?.client?.client_name} ·{' '}
                            {item?.task?.project?.project} ·{' '}
                            {`${item.assigner?.first_name} ${item.assigner?.last_name}`}{' '}
                            · {`T${item.task_id}`}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24} className={styles.RowStyles}>
                        <Col span={12}>
                          <Space>
                            {/* Commenting out because users did not find this useful */}

                            {/* <CustomButton
                              className={styles.primaryButton}
                              size="small"
                              onClick={() => StartTask(item?.id, 'started')}
                              disabled={item?.is_started}
                            >
                              <Space size={4}>
                                <CaretRightOutlined />{' '}
                                {item?.is_started ? 'Started' : 'Start'}
                              </Space>
                            </CustomButton> */}
                            <CustomButton
                              className={styles.stuckButton}
                              size="small"
                              disabled={!isCurrentDateToday}
                              onClick={() => handleStuck(item?.id, 'escalated')}
                            >
                              <Space size={4}>
                                <WarningOutlined /> Stuck
                              </Space>
                            </CustomButton>
                            <CustomButton
                              className={styles.doneButton}
                              size="small"
                              disabled={!isCurrentDateToday}
                              onClick={() =>
                                handleStuck(
                                  item?.id,
                                  'completed',
                                  item?.task?.title
                                )
                              }
                            >
                              <Space size={4}>
                                <CheckOutlined /> Done
                              </Space>
                            </CustomButton>
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
            </Card>
          </Col>
        </Row>
      ) : (
        ''
      )}
      {escalatedTasks?.length ? (
        <Row gutter={24} className={`${styles.RowStyles} !pb-8`}>
          <Col span={24}>
            <Card className={styles.taskCard} bordered={false}>
              <Row gutter={24}>
                <Col span={20}>
                  <Title
                    level={5}
                    className={styles.taskTitle}
                    style={{ margin: 0 }}
                  >
                    <Space>
                      {<WarningOutlined className={styles.stuckIcon} />}
                      {`Escalated Tasks`}
                    </Space>
                  </Title>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                  <Space align="center" size={4} className={styles.caret1}>
                    <Badge
                      count={parseInt(escalatedTasks?.length)}
                      className={styles.WarningBadgeIconStyle}
                    />
                    {escalatedTasksVisible ? (
                      <DownOutlined onClick={toggleescalatedTasks} />
                    ) : (
                      <UpOutlined onClick={toggleescalatedTasks} />
                    )}
                  </Space>
                </Col>
              </Row>
              {escalatedTasksVisible &&
                escalatedTasks?.map((item, index) => {
                  const isAdHOc = item.task.task_type === 'ad_hoc';
                  return (
                    <Card
                      className={styles.escalated_nestedCard}
                      bordered={false}
                      key={index}
                    >
                      <Row gutter={24}>
                        <Col
                          span={24}
                          className="!flex !items-center !justify-between"
                        >
                          <Title
                            level={5}
                            className={styles.taskTitle}
                            style={{ margin: 0 }}
                          >
                            <Space>{item?.task?.title}</Space>
                          </Title>
                          {isAdHOc && (
                            <Tag
                              color="purple"
                              className="!m-0"
                              bordered={false}
                            >
                              Ad-hoc
                            </Tag>
                          )}
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.descriptionText}
                          >
                            {item?.task?.description}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.secondaryText}
                          >
                            {item?.task?.client?.client_name} ·{' '}
                            {item?.task?.project?.project} ·{' '}
                            {`${item.assigner?.first_name} ${item.assigner?.last_name}`}{' '}
                            · {`T${item.task_id}`}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24} className={styles.RowStyles}>
                        <Col span={12}>
                          <Space>
                            {/* Commenting out because users did not find this useful */}

                            {/* <CustomButton
                              className={styles.primaryButton}
                              size="small"
                              onClick={() => StartTask(item?.id, 'started')}
                              disabled={item?.is_started}
                            >
                              <Space size={4}>
                                <CaretRightOutlined />
                                {item?.is_started ? 'Started' : 'Start'}
                              </Space>
                            </CustomButton> */}
                            <CustomButton
                              className={styles.doneButton}
                              size="small"
                              disabled={!isCurrentDateToday}
                              onClick={() =>
                                handleStuck(
                                  item?.id,
                                  'completed',
                                  item?.task?.title
                                )
                              }
                            >
                              <Space size={4}>
                                <CheckOutlined /> Done
                              </Space>
                            </CustomButton>
                          </Space>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
            </Card>
          </Col>
        </Row>
      ) : (
        ''
      )}
      {completeTasks?.length ? (
        <Row gutter={24} className={`${styles.RowStyles} !pb-8`}>
          <Col span={24}>
            <Card className={styles.taskCard} bordered={false}>
              <Row gutter={24}>
                <Col span={20}>
                  <Title
                    level={5}
                    className={styles.taskTitle}
                    style={{ margin: 0 }}
                  >
                    <Space>
                      {<CheckCircleFilled className={styles.checkIcon} />}
                      {`Completed Tasks`}
                    </Space>
                  </Title>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                  <Space align="center" size={4} className={styles.caret}>
                    <Badge
                      count={parseInt(completeTasks?.length)}
                      className={styles.SuccessBadgeIconStyle}
                    />
                    {completedTasksVisible ? (
                      <DownOutlined onClick={toggleCompletedTasks} />
                    ) : (
                      <UpOutlined onClick={toggleCompletedTasks} />
                    )}
                  </Space>
                </Col>
              </Row>
              {completedTasksVisible &&
                completeTasks?.map((item, index) => {
                  const isAdHOc = item.task.task_type === 'ad_hoc';
                  return (
                    <Card
                      className={styles.nestedCard}
                      bordered={false}
                      key={index}
                    >
                      <Row gutter={24}>
                        <Col
                          span={24}
                          className="!flex !items-center !justify-between"
                        >
                          <Title
                            level={5}
                            className={styles.taskTitle}
                            style={{ margin: 0 }}
                          >
                            <Space>{item?.task?.title}</Space>
                          </Title>
                          {isAdHOc && (
                            <Tag
                              color="purple"
                              className="!m-0"
                              bordered={false}
                            >
                              Ad-hoc
                            </Tag>
                          )}
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.descriptionText}
                          >
                            {item?.task?.description}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.secondaryText}
                          >
                            {item?.task?.client?.client_name} ·{' '}
                            {item?.task?.project?.project} ·{' '}
                            {`${item.assigner?.first_name} ${item.assigner?.last_name}`}{' '}
                            · {`T${item.task_id}`}
                          </Text>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
            </Card>
          </Col>
        </Row>
      ) : (
        ''
      )}
      {showModel ? (
        <CustomModal
          // title="Create Department"
          visible={showModel}
          // width="30%"
          footer={null}
          className={`!w-full sm:!w-[60%] md:!w-[40%] lg:!w-[35%] xl:!w-[30%] ${styles.customModal}`}
          closable={false}
        >
          {planStatus === 'completed' ? (
            <Form form={form} layout="vertical">
              <h3 className={styles.mainTitle}>{statusTitle}</h3>
              <br />
              <FormItem
                label="Rate the difficulty level"
                name="difficulty"
                rules={[{ required: true, message: 'Difficulty is required' }]}
                className={styles.customLable}
              >
                <Radio.Group
                  buttonStyle="solid"
                  size="small"
                  onChange={(e) => handleReason(e)}
                >
                  <Space>
                    <Radio.Button value="easy">
                      {' '}
                      <SmileOutlined className={styles.smile} /> Easy
                    </Radio.Button>
                    <Radio.Button value="challenging">
                      {' '}
                      <LikeOutlined className={styles.frown} /> Challenging
                    </Radio.Button>
                  </Space>
                </Radio.Group>
              </FormItem>
              <FormItem
                label="Did you use AI to do this task?"
                name="ai_use"
                rules={[{ required: true, message: 'This is required field' }]}
                className={styles.customLable}
              >
                <Radio.Group
                  buttonStyle="solid"
                  size="small"
                  onChange={(e) => handleAIConfirm(e)}
                >
                  <Space>
                    <Radio.Button value="yes">Yes</Radio.Button>
                    <Radio.Button value="no">No</Radio.Button>
                  </Space>
                </Radio.Group>
              </FormItem>
              <FormItem
                label="Completion notes"
                rules={[{ required: true }]}
                className={styles.customLable}
              >
                <TextArea
                  rows={4}
                  placeholder="Completion notes"
                  onChange={(e) => handleOtherReasonChange(e)}
                />
              </FormItem>
              <br />
              <FormItem className="text-right">
                <Space>
                  <CustomButton
                    className={styles.inwardButton}
                    onClick={() => handleClose()}
                    type="primary"
                    ghost
                  >
                    Close
                  </CustomButton>
                  <CustomButton
                    className={styles.inwardButton}
                    htmlType="submit"
                    type="primary"
                    onClick={() => handleSubmit()}
                    loading={loading}
                  >
                    Submit
                  </CustomButton>
                </Space>
              </FormItem>
            </Form>
          ) : (
            <Form form={form} layout="vertical">
              {}
              <h3 className={styles.mainTitle}>{`What's blocking you?`}</h3>
              <span
                className={styles.desription}
              >{`Select the reason you're stuck or specify your own.`}</span>
              <br />
              <br />
              {stuck_reasons.map((item, index) => {
                return (
                  <Row
                    key={index}
                    align="middle"
                    style={{
                      padding: '4px 8px 4px 8px',
                      backgroundColor: '#fff',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px',
                      marginBottom: '5px'
                    }}
                  >
                    <Space size={20}>
                      <Radio.Group value={reason}>
                        <Radio
                          value={item.value}
                          onChange={() => handleReasonChange(item.value)}
                          className={styles.projectText}
                        >
                          <Text>{item?.label}</Text>
                        </Radio>
                      </Radio.Group>
                    </Space>
                  </Row>
                );
              })}
              {reason === 'other' && (
                <div className="mt-4 mb-4">
                  <TextArea
                    rows={4}
                    placeholder="Describe whats block you"
                    onChange={(e) => handleOtherReasonChange(e)}
                  />
                </div>
              )}
              <br />
              <FormItem className="text-right">
                <Space>
                  <CustomButton
                    className={styles.inwardButton}
                    onClick={() => handleClose()}
                    type="primary"
                    ghost
                  >
                    Close
                  </CustomButton>
                  <CustomButton
                    className={styles.inwardButton}
                    htmlType="submit"
                    type="primary"
                    onClick={() => handleSubmit()}
                    loading={loading}
                  >
                    Escalate
                  </CustomButton>
                </Space>
              </FormItem>
            </Form>
          )}
        </CustomModal>
      ) : (
        ''
      )}

      {!priorityTasks.length &&
      !pendingTasks.length &&
      !escalatedTasks.length &&
      !completeTasks.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No tasks assigned for today"
          className="sm:-translate-x-1/2 sm:fixed sm:-translate-y-1/2 top-1/2 left-1/2"
        />
      ) : null}
    </>
  );
};

export default TaskTracker;
